import React, { useEffect, useState } from 'react';
import CoverArt from '../CoverArt';
import { entityId } from '../../utils/entityId';

// DESIGN V2 — the Network tab's expanded row: which of the subject's tracks
// this DJ played (or which of this artist's tracks the subject played), each
// with the mixes and dates. Same data as the artists list modal's expansion
// (searchCoPlayedTracks), rendered inline under the row instead of in an
// overlay. Fetches once on mount; the parent keeps it mounted while open.
// Optional (label page): `countLabel(track)` puts a count at the track's right
// end, a track's `mixesTotal` above its listed mixes adds "+ N more mixes"
// (opens the track), and `pageable` turns the "Showing x of y" line into a
// "Show more" button — fetchTracksFor(artist, page) is then called per page.
export default function CoPlayedTracks({ artist, fetchTracksFor, onTrackClick, onMixClick, countLabel, pageable = false }) {
    const [state, setState] = useState({ loading: true, tracks: [], total: null, error: null, page: 1 });
    useEffect(() => {
        let alive = true;
        setState({ loading: true, tracks: [], total: null, error: null, page: 1 });
        Promise.resolve(fetchTracksFor(artist, 1))
            .then((r) => { if (alive) setState({ loading: false, tracks: r?.tracks || [], total: r?.total ?? null, error: null, page: 1 }); })
            .catch((e) => { if (alive) setState({ loading: false, tracks: [], total: null, error: e?.message || 'Failed to load', page: 1 }); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId(artist), artist?.name]);
    const loadMore = () => {
        const page = state.page + 1;
        setState((s) => ({ ...s, loading: true }));
        Promise.resolve(fetchTracksFor(artist, page))
            .then((r) => setState((s) => ({ ...s, loading: false, tracks: [...s.tracks, ...(r?.tracks || [])], total: r?.total ?? s.total, page })))
            .catch((e) => setState((s) => ({ ...s, loading: false, error: e?.message || 'Failed to load' })));
    };

    return (
        <div className="v2-network-detail djpal-artists-list-modal-tracks">
            {state.loading && state.tracks.length === 0 && <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>}
            {state.error && <div className="djpal-empty-hint">Couldn't load tracks: {state.error}</div>}
            {!state.loading && !state.error && state.tracks.length === 0 && <div className="djpal-empty-hint">No tracks found.</div>}
            {state.tracks.map((t, ti) => {
                const art = t.artworkUrl || t.artwork || '';
                const label = t.artist ? `${t.artist} – ${t.title}` : t.title;
                const mixes = Array.isArray(t.mixes) ? t.mixes : [];
                return (
                    <div key={`${entityId(t) || ti}`} className="djpal-artists-list-modal-track-row">
                        <div className="djpal-artists-list-modal-track clickable" onClick={(e) => { e.stopPropagation(); onTrackClick?.(t); }} title={label}>
                            {art ? <CoverArt className="track-artwork-mini" src={art} size={28} /> : <div className="track-artwork-mini placeholder">&#9835;</div>}
                            <span className="track-label">{label}</span>
                            {countLabel && countLabel(t) && <span className="v2-row-meta co-played-count">{countLabel(t)}</span>}
                        </div>
                        {mixes.length > 0 && (
                            <div className="djpal-artists-list-modal-mixes">
                                {mixes.map((mx, mi) => {
                                    const titleParts = [mx.artist, mx.eventName].filter(Boolean);
                                    const mixLabel = titleParts.join(mx.separator ? ` ${mx.separator} ` : ' – ') || mx.eventName || '(untitled mix)';
                                    return (
                                        <div key={`${entityId(mx) || mi}`} className="djpal-artists-list-modal-mix clickable" onClick={(e) => { e.stopPropagation(); onMixClick?.(mx); }} title={mixLabel}>
                                            {mx.date && <span className="djpal-artists-list-modal-mix-date">{mx.date}</span>}
                                            <span className="djpal-artists-list-modal-mix-label">{mixLabel}</span>
                                        </div>
                                    );
                                })}
                                {t.mixesTotal > mixes.length && (
                                    <div className="djpal-artists-list-modal-mix clickable co-played-more" onClick={(e) => { e.stopPropagation(); onTrackClick?.(t); }}>
                                        <span className="djpal-artists-list-modal-mix-label">+ {(t.mixesTotal - mixes.length).toLocaleString()} more {t.mixesTotal - mixes.length === 1 ? 'mix' : 'mixes'}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
            {state.total != null && state.total > state.tracks.length && (pageable ? (
                <button type="button" className="v2-btn sm quiet co-played-more-tracks" disabled={state.loading} onClick={(e) => { e.stopPropagation(); loadMore(); }}>
                    {state.loading ? 'Loading…' : `Show more tracks (${(state.total - state.tracks.length).toLocaleString()} more)`}
                </button>
            ) : (
                <div className="djpal-empty-hint">Showing {state.tracks.length} of {state.total}</div>
            ))}
        </div>
    );
}
